'use client'

import React from 'react'

import { VCardPreviewType } from '@/lib/types/VCardT'
import { PAPER } from '@/components/EmailSignaturePreview/signature'
import ClassicSignature from '@/components/EmailSignaturePreview/ClassicSignature'
import ContactCardPreview from '@/components/ContactLinkCard'
import IOSContactPreview from '@/components/IOSContactPreview'
import IPhone15Pro from '@/components/IPhone15Pro'
import QrCard from '@/components/livecontact/QrCard'

/**
 * The four assets the hero carousel shows. Each one is the *real* component the product
 * generates — the profile card, the iOS contact sheet, the branded QR and the email
 * signature — fed the same preview record, so the landing page can never promise something
 * the app doesn't actually build.
 */

type SlideProps = { data: VCardPreviewType | null; previewSlug?: string }

/** Public profile link for the card and the QR code. */
export const profileUrl = (data: VCardPreviewType | null, previewSlug?: string) => {
  if (previewSlug) return `https://livecontact.com/${previewSlug}`
  const site = data?.website?.replace(/^https?:\/\//, '').replace(/\/$/, '')
  return site ? `https://${site}` : 'https://livecontact.com'
}

/**
 * Slide 1 — the profile page, shown whole. It used to be clipped with a bottom fade, which ate
 * the link preview and contact rows: the part of the card that proves what a profile page does.
 *
 * The card fits the stage as it stands; expanding a long description with "Read more" is the one
 * thing that can outgrow it, so the column scrolls (bars hidden) instead of pushing the card out
 * through the panel's edges.
 */
export const ProfileSlide = ({ data }: SlideProps) => (
  <div className="lc-scrollbar-none -m-3 max-h-full w-[368px] overflow-y-auto p-3">
    <ContactCardPreview data={data} />
  </div>
)

/** Slide 2 — the iOS contact card, inside the phone frame. */
export const PhoneSlide = ({ data }: SlideProps) => (
  <IPhone15Pro>
    <IOSContactPreview data={data} isPriority />
  </IPhone15Pro>
)

/** Slide 3 — the branded QR code on its paper card. */
export const QrSlide = ({ data, previewSlug }: SlideProps) => {
  const url = profileUrl(data, previewSlug)
  return (
    <QrCard
      value={url}
      size={248}
      paper
      logo={typeof data?.avatar === 'string' ? data.avatar : null}
      logoTone={data?.mainColor}
      caption={url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
      className="w-[300px]"
    />
  )
}

/**
 * Slide 4 — the email signature, on the white message body it is pasted onto. The mail-client
 * chrome above it is the same mock the signature tab uses, so the sign-off is shown in context
 * rather than floating on its own.
 *
 * The card's description is dropped here — the signature template still supports it, but a
 * paragraph-long blurb under a sign-off buries the name and the contact rows, which are the
 * point of the slide. The full description is shown on the profile slide, where it belongs.
 */
export const SignatureSlide = ({ data, previewSlug }: SlideProps) => (
  <div
    className="w-[420px] overflow-hidden rounded-[20px] bg-white shadow-pop"
    style={{ color: PAPER.ink }}
  >
    <div
      className="flex flex-col gap-1 px-5 py-4"
      style={{ borderBottom: `1px solid ${PAPER.hairlineSoft}` }}
    >
      <p className="text-[13px] font-semibold leading-[1.3] tracking-[-0.2px]">
        Great meeting you today
      </p>
      <p className="text-[11px] leading-[1.4] tracking-[-0.2px]" style={{ color: PAPER.inkSubtle }}>
        To: jane@example.com
      </p>
    </div>

    <div className="flex flex-col gap-2 px-5 pt-4">
      <span className="block h-[7px] w-[240px] rounded-full" style={{ background: PAPER.chip }} />
      <span className="block h-[7px] w-[188px] rounded-full" style={{ background: PAPER.chip }} />
      <p className="pt-2 text-[12px] leading-[1.4] tracking-[-0.2px]">Best,</p>
    </div>

    <div className="px-5 pb-5 pt-3">
      <ClassicSignature
        {...(data ?? {})}
        notes=""
        contactDownloadUrl={profileUrl(data, previewSlug)}
      />
    </div>
  </div>
)
